(() => {
  const TARGET_SELECTOR = '[data-mc-product-gallery-target]';
  const PREVIEW_SELECTOR = '.tee-gallery-content, .tee-gallery, #tee-gallery';
  const VISUAL_SELECTOR = 'img, canvas, video';

  const isReadyVisual = (element) => {
    if (element instanceof HTMLImageElement) {
      return Boolean(element.currentSrc || element.src) && element.complete && element.naturalWidth > 0;
    }

    if (element instanceof HTMLCanvasElement) {
      return element.width > 0 && element.height > 0;
    }

    if (element instanceof HTMLVideoElement) {
      return element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
    }

    return false;
  };

  const getVisualSignature = (visuals) =>
    visuals
      .map((element) => {
        if (element instanceof HTMLImageElement) {
          return `image:${element.currentSrc || element.src}`;
        }

        if (element instanceof HTMLCanvasElement) {
          return `canvas:${element.width}x${element.height}`;
        }

        if (element instanceof HTMLVideoElement) {
          return `video:${element.currentSrc || element.src}:${element.readyState}`;
        }

        return '';
      })
      .filter(Boolean)
      .join('|');

  const createToolbar = (target) => {
    const existingToolbar = target.previousElementSibling;
    if (existingToolbar?.matches('[data-mc-gallery-toolbar]')) return existingToolbar;

    const toolbar = document.createElement('div');
    toolbar.className = 'mc-product-gallery-toolbar';
    toolbar.dataset.mcGalleryToolbar = '';
    toolbar.hidden = true;
    toolbar.innerHTML = `
      <div class="mc-product-gallery-switcher" data-mc-gallery-switcher role="group" aria-label="Product media view" hidden>
        <button type="button" class="mc-product-gallery-switcher__button" data-mc-gallery-view-button="preview" aria-pressed="false">
          Your preview
        </button>
        <button type="button" class="mc-product-gallery-switcher__button" data-mc-gallery-view-button="product" aria-pressed="true">
          Product photos
        </button>
      </div>
      <p class="mc-product-gallery-status" data-mc-gallery-status aria-live="polite" hidden>
        Updating your preview…
      </p>
    `;

    target.before(toolbar);
    return toolbar;
  };

  const initializeGallery = (target) => {
    if (!(target instanceof HTMLElement) || target.dataset.mcGalleryController === 'true') return;

    target.dataset.mcGalleryController = 'true';
    target.dataset.mcGalleryView = 'product';

    const toolbar = createToolbar(target);
    const switcher = toolbar.querySelector('[data-mc-gallery-switcher]');
    const status = toolbar.querySelector('[data-mc-gallery-status]');
    const buttons = Array.from(toolbar.querySelectorAll('[data-mc-gallery-view-button]'));

    let previewReady = false;
    let lastSignature = '';
    let evaluationFrame = 0;

    const setView = (view) => {
      const nextView = view === 'preview' && previewReady ? 'preview' : 'product';
      target.dataset.mcGalleryView = nextView;

      buttons.forEach((button) => {
        const isActive = button.dataset.mcGalleryViewButton === nextView;
        button.setAttribute('aria-pressed', String(isActive));
      });
    };

    const evaluate = () => {
      evaluationFrame = 0;
      if (!target.isConnected) return;

      const nativeGallery = target.querySelector(':scope > media-gallery');
      nativeGallery?.classList.add('mc-product-gallery-native');

      const previewContainers = Array.from(target.querySelectorAll(PREVIEW_SELECTOR));
      previewContainers.forEach((container) => container.classList.add('mc-product-gallery-preview'));

      const previewVisuals = previewContainers.flatMap((container) =>
        Array.from(container.querySelectorAll(VISUAL_SELECTOR)).filter(
          (visual) => !visual.closest('.mc-product-gallery-native')
        )
      );

      previewVisuals.forEach((visual) => {
        visual.classList.add('mc-product-gallery-preview-visual');

        if (visual instanceof HTMLImageElement && !visual.complete) {
          visual.addEventListener('load', scheduleEvaluation, { once: true });
          visual.addEventListener('error', scheduleEvaluation, { once: true });
        }

        if (visual instanceof HTMLVideoElement) {
          visual.addEventListener('loadeddata', scheduleEvaluation, { once: true });
        }
      });

      const readyVisuals = previewVisuals.filter(isReadyVisual);
      const nextPreviewReady = readyVisuals.length > 0;
      const signature = nextPreviewReady ? getVisualSignature(readyVisuals) : '';
      const previewWasUpdated = nextPreviewReady && signature && signature !== lastSignature;

      previewReady = nextPreviewReady;
      target.classList.toggle('is-preview-ready', previewReady);
      switcher.hidden = !previewReady;

      const hasPreviewContainer = previewContainers.length > 0;
      toolbar.hidden = !hasPreviewContainer;
      status.hidden = !hasPreviewContainer || previewReady;

      if (!previewReady) {
        lastSignature = '';
        setView('product');
        return;
      }

      if (previewWasUpdated) {
        lastSignature = signature;
        setView('preview');
      }
    };

    function scheduleEvaluation() {
      if (evaluationFrame) return;
      evaluationFrame = window.requestAnimationFrame(evaluate);
    }

    buttons.forEach((button) => {
      button.addEventListener('click', () => setView(button.dataset.mcGalleryViewButton));
    });

    const observer = new MutationObserver(scheduleEvaluation);
    observer.observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset', 'class', 'style', 'width', 'height'],
    });

    evaluate();
    window.setTimeout(scheduleEvaluation, 250);
    window.setTimeout(scheduleEvaluation, 1000);
    window.setTimeout(scheduleEvaluation, 2500);
  };

  const initializeAll = (root = document) => {
    if (root instanceof Element && root.matches(TARGET_SELECTOR)) initializeGallery(root);
    root.querySelectorAll?.(TARGET_SELECTOR).forEach(initializeGallery);
  };

  initializeAll();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));

  const pageObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) initializeAll(node);
      });
    });
  });

  pageObserver.observe(document.documentElement, { childList: true, subtree: true });
})();

// Mobile: pin the Teeinblue preview gallery while the personalization form is on screen.
(() => {
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const MEDIA_SELECTOR = '.product-information__grid > .product-information__media';
  const FORM_SELECTOR = '.tee-customize-main-form';
  const GALLERY_SELECTOR = '.tee-gallery-content';
  const PINNED_CLASS = 'mc-preview-pinned';
  const FORM_TRIGGER_RATIO = 0.6;
  const PINNED_PREVIEW_RATIO = 0.3;

  const ANIMATION_MS = 400;

  let spacer = null;
  let frame = 0;
  let leaveTimer = 0;

  const getTopOffset = () => {
    const header = document.querySelector('[data-mc-header]');
    if (!header) return 0;
    const position = getComputedStyle(header).position;
    if (position !== 'sticky' && position !== 'fixed') return 0;
    return Math.max(0, header.getBoundingClientRect().bottom);
  };

  const unpin = (media) => {
    window.clearTimeout(leaveTimer);
    media?.classList.remove(PINNED_CLASS, 'is-animating', 'is-entering', 'is-leaving');
    media?.style.removeProperty('--mc-pin-top');
    media?.style.removeProperty('--mc-pin-shift');
    media?.style.removeProperty('--mc-pin-zoom');
    media?.style.removeProperty('--mc-pin-width');
    spacer?.remove();
    spacer = null;
  };

  const update = () => {
    frame = 0;

    const media = document.querySelector(MEDIA_SELECTOR);
    const form = document.querySelector(FORM_SELECTOR);
    const details = media?.parentElement?.querySelector(':scope > .product-details');
    const hasPreviewGallery = media?.querySelector(GALLERY_SELECTOR);

    if (!MOBILE.matches || !media || !form || !details || !hasPreviewGallery) {
      unpin(media);
      return;
    }

    const top = getTopOffset();
    const pinned = media.classList.contains(PINNED_CLASS);
    const naturalTop = (pinned && spacer ? spacer : media).getBoundingClientRect().top;
    const formTop = form.getBoundingClientRect().top;
    const detailsBottom = details.getBoundingClientRect().bottom;

    const shouldPin = naturalTop <= top && formTop < window.innerHeight * FORM_TRIGGER_RATIO && detailsBottom > top;
    if (!shouldPin) {
      if (!pinned) return;
      // Leaving through the end of the details has already slid out with the scroll.
      if (detailsBottom <= top) return unpin(media);
      if (media.classList.contains('is-leaving')) return;
      media.classList.add('is-animating', 'is-leaving');
      window.clearTimeout(leaveTimer);
      leaveTimer = window.setTimeout(() => unpin(media), ANIMATION_MS);
      return;
    }

    if (media.classList.contains('is-leaving')) {
      window.clearTimeout(leaveTimer);
      media.classList.remove('is-leaving');
      leaveTimer = window.setTimeout(() => media.classList.remove('is-animating'), ANIMATION_MS);
    }

    if (!pinned) {
      const stage = media.querySelector('.tee-gallery__stage');
      const zoom = stage ? Math.min(1, (window.innerHeight * PINNED_PREVIEW_RATIO) / stage.offsetHeight) : 1;
      media.style.setProperty('--mc-pin-zoom', zoom);
      media.style.setProperty('--mc-pin-width', `${stage?.offsetWidth ?? media.offsetWidth}px`);
      spacer = document.createElement('div');
      spacer.className = 'mc-preview-pin-spacer';
      spacer.style.height = `${media.offsetHeight}px`;
      media.before(spacer);
      media.classList.add(PINNED_CLASS, 'is-entering');
      media.getBoundingClientRect();
      media.classList.add('is-animating');
      media.classList.remove('is-entering');
      window.clearTimeout(leaveTimer);
      leaveTimer = window.setTimeout(() => media.classList.remove('is-animating'), ANIMATION_MS);
    }

    media.style.setProperty('--mc-pin-top', `${top}px`);
    media.style.setProperty('--mc-pin-shift', `${Math.min(0, detailsBottom - top - media.offsetHeight)}px`);
  };

  const schedule = () => {
    if (!frame) frame = window.requestAnimationFrame(update);
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  MOBILE.addEventListener('change', schedule);
  document.addEventListener('shopify:section:load', schedule);
  schedule();
})();
